<!-- Copyright 2023, Common Good Learning Tools LLC -->
<template><k-dialog v-model="dialog_open" max-width="900" persistent scrollable aria-labelledby="satchel-exchange-register-title" @keydown.esc="$emit('dialog_cancel')"><k-card>
	<k-card-title style="background-color:#ddd"><k-icon color="#000" class="mr-3">fas fa-list-check</k-icon><b id="satchel-exchange-register-title">Satchel Rosetta Exchange: Registration</b></k-card-title>
	<k-card-text class="mt-1" style="font-size:16px; line-height:21px;">
		<div class="pink lighten-5 pa-2 mt-2 mb-3 black--text" style="border-radius:6px;">
			<div>Common Good Learning Tools (CGLT) provides <b>free browsing access</b> to all competency frameworks available on Rosetta Exchange: no registration or sign-in required.</div>
			<div class="mt-3">However, if you would like to <b>download</b> competency framework data from Rosetta Exchange, or access competency data via from Rosetta Exchange <b>CASE® APIs</b>, you must <b>register</b> for the Exchange and be approved by CGLT.</div>
		</div>

		<div class="d-flex align-center justify-center mt-6">
			<div class="text-right mr-4">
				To start the registration process, please tell us
				<br>what <b>type of organization</b> you are with:</div>
			<div style="width:200px">
				<v-select v-model="organization_type" :items="organization_types" label="*Organization Type" outlined dense hide-details append-icon="fas fa-caret-down"></v-select>
			</div>
		</div>

		<div v-if="organization_type" class="mt-6">

			<p v-if="organization_type=='public_agency'">CGLT is pleased to be able to provide <b>free</b> download access to staff of <b>public agencies</b> (e.g. state DOEs, country MOEs, schools, school districts, etc.).</p>
			
			<p v-else>For <b>ed-tech companies</b> and <b>non-public agencies</b>: 
				<ul>
					<li class="mt-2">Rosetta can essentially serve as your organization’s <b>database for learning standards</b> and other competencies from all 50 US states, many other countries, and a host of non-governmental organizations.</li>
					<li class="mt-2">CGLT asks companies and non-public agencies to pay a modest monthly fee for registered access. These fees cover CGLT’s costs for maintaining the Exchange, as well as coding, publishing, and maintaining many of the frameworks on the Exchange, for the benefit of the ed-tech ecosystem.</li>
					<li class="mt-2"><b class="pink--text text--darken-3">For pricing options and more information about the benefits of registered access to Rosetta Exchange, <nobr>please <a :href="site_config.exchange_pricing_options_url" target="_blank">CLICK HERE</a>.</nobr></b></li>
				</ul>
			</p>
			
			<div class="pink lighten-5 pa-2 mt-2 mb-3 black--text" style="border-radius:6px;"><b>To complete the registration process</b>, please submit the following information, and someone from CGLT will get back to you within a week. You can also email <a href="mailto:support@commongoodlt.com?subject=Satchel Rosetta Exchange inquiry"><b>support@commongoodlt.com</b></a> with questions.</div>

			<div class="d-flex mt-5">
				<k-text-field class="mr-3" outlined dense hide-details label="*Your organization’s name" v-model="organization" placeholder=""></k-text-field>
				<k-text-field outlined dense hide-details label="*Your job title" v-model="org_job_title" placeholder=""></k-text-field>
			</div>

			<div class="d-flex mt-5">
				<div style="flex:0 0 28%"><k-text-field class="mr-3" outlined dense hide-details label="*Your first name" v-model="first_name" placeholder=""></k-text-field></div>
				<div style="flex:0 0 28%"><k-text-field class="mr-3" outlined dense hide-details label="*Your last name" v-model="last_name" placeholder=""></k-text-field></div>
				<div style="flex:0 0 44%"><k-text-field outlined dense hide-details="auto" label="*Your email" v-bind="formFields.email" @input="formFields.email.input($event); email = $event" @blur="formFields.email.blur" placeholder=""></k-text-field></div>
			</div>

			<div v-if="organization_type=='public_agency'" class="d-flex mt-6 mx-auto" style="width:800px;">
				<div style="flex:0 0 45%" class="text-right pr-3">*Select the category of frameworks<br>you would like download/API access to:</div>
				<div style="flex:0 0 55%">
					<v-select v-model="category_access_requested" :items="free_category_filter_items" label="*Framework category" outlined dense hide-details append-icon="fas fa-caret-down"></v-select>
					<v-spacer/>
				</div>
			</div>

			<div v-else class="d-flex justify-center mt-6">
				<div class="text-right pr-3">
					*Select the <a :href="site_config.exchange_pricing_options_url" target="_blank"><b>access level</b></a> your organization would like to
					<br>initially register for (you can update this later):</div>
				<div style="max-width:400px;">
					<v-select v-model="category_access_requested" :items="paid_category_filter_items" label="*Access level" outlined dense hide-details append-icon="fas fa-caret-down"></v-select>
				</div>
			</div>

			<div class="text-center mt-6 mx-auto" style="width:600px;">
				<k-textarea outlined dense hide-details label="*Let us know, in a sentence or two, how you plan to use Rosetta Exchange" rows="3" v-model="reason" placeholder=""></k-textarea>
			</div>
			
			<div class="d-flex mt-6">
				<k-checkbox class="mt-0 pt-0 d-inline-block" style="vertical-align:bottom" hide-details off-icon="far fa-square" on-icon="fas fa-check-square"
					v-model="tos_clicked"
				><template v-slot:label><span>To submit your registration request, please read and agree with the Rosetta Exchange <a @click.stop="show_tos"><b>TERMS OF SERVICE</b>*</a></span></template>
				</k-checkbox>
			</div>
		</div>

	</k-card-text>
	<k-card-actions class="pa-3" style="border-top:1px solid #999; background-color:#ddd">
		<k-btn color="secondary" @click="$emit('dialog_cancel')"><k-icon small class="mr-2">fas fa-xmark</k-icon>Cancel</k-btn>
		<v-spacer/>
		<div style="font-size:14px" class="mr-2">*All fields are required</div>
		<k-btn color="primary" :disabled="!form_complete" @click="formHandleSubmit">Submit Registration Request <k-icon small class="ml-2">fas fa-circle-arrow-right</k-icon></k-btn>
	</k-card-actions>
</k-card></k-dialog></template>

<script>
import { mapState, mapGetters } from 'vuex'
import { useFormMixin } from '@cglt/ui-legacy'
import { z } from 'zod'

export default {
	mixins: [
		useFormMixin({
			schema: z.object({
				email: z.email('Please enter a valid email address.'),
			}),
			initialValues: {
				email: '',
			},
			onSubmit: function () {
				this.submit()
			},
		})
	],
	props: {
		// req: { type: String, required: true },
		// nreq: { type: String, required: false, default() { return ''} },
	},
	data() { return {
		dialog_open: true,

		tos_clicked: false,

		// user can request access to something else if they wish; or a user originally from CN2 can register for access to something
		first_name: vapp.user_info.first_name ?? '',
		last_name: vapp.user_info.last_name ?? '',
		email: vapp.user_info.email ?? '',
		organization: vapp.user_info.organization ?? '',
		organization_type: '',
		org_job_title: '',
		category_access_requested: '',
		reason: '',

		// first_name: 'Sima',
		// last_name: 'Lation',
		// email: 'simalation503@gmail.com',
		// organization: 'Negative Campus',
		// organization_type: '',
		// org_job_title: 'Programmerr',
		// reason: 'I need to do it',
		// category_access_requested: '',
		// tos_clicked: true,

		// keep this in sync with AdminUserEdit and CommonsRegister
		organization_types: [
			{ value: 'public_agency', text: 'Public agency (e.g. state DOE, country MOE, school, school district)' },
			{ value: 'non_public_agency', text: 'Non-public agency (e.g. charter school network, nonprofit organization)' },
			{ value: 'ed_tech_company', text: 'Ed-tech company' },
		],
	}},
	computed: {
		...mapState(['site_config', 'user_info']),
		...mapGetters([]),
		form_complete() {
			if (empty(this.first_name.trim())) return false
			if (empty(this.last_name.trim())) return false
			if (empty(this.email.trim())) return false
			if (empty(this.organization.trim())) return false
			if (empty(this.organization_type.trim())) return false
			if (empty(this.org_job_title.trim())) return false
			if (empty(this.category_access_requested.trim())) return false
			if (empty(this.reason.trim())) return false
			if (!this.tos_clicked) return false
			return true
		},

		// keep these in sync with AdminUserEdit
		free_category_filter_items() {
			let category_records = U.create_category_records(this.$store.getters.filtered_framework_records, false, false)
			let arr = []
			arr.push({value: '', text: '-----------------------------'})
			for (let cr of category_records) {
				if (empty(cr.map_label)) continue
				arr.push({
					value: cr.map_label,
					text: cr.title
				})
			}
			return arr
		},

		paid_category_filter_items() {
			let arr = []
			arr.push({value: '', text: '-----------------------------'})
			arr.push({value: 'ALL', text: 'Standard (All frameworks; $700/month; 12-month commitment)'})
			arr.push({value: 'CORE4', text: 'Core-Four (All US state “core-4” frameworks; $500/month; 6-month commitment'})
			arr.push({value: 'TRIAL', text: 'Trial (One state or agency; $500; one-time fee)'})
			return arr
		},
	},
	watch: {
	},
	created() {
	},
	mounted() {
		vapp.exchange_register_component = this
	},
	methods: {
		show_tos() { 
			vapp.satchel_tos_showing = true 
			setTimeout(x=>this.tos_clicked = false)
		},
		submit() {
			// validation happened above
			let email_body_html = 
				`A ${this.site_config.app_name} user account has been created for this email address (${this.email.trim()}).<br/><br/>`
				+ `Click the following link to sign in for the first time. This link can only be used once, and will expire in 24 hours. After clicking the link to sign in, you can set or change your account password. (If you fail to click this link within 24 hours, you can always click the SIGN IN button from ${this.site_config.app_name}, then click the “Forgot Password” link to receive a new sign-in link.)<br/><br/>`
				+ `Your Registration request for download and/or API access to ${this.site_config.app_name} frameworks has been sent to Common Good Learning Tools for review, and you should hear back from us within a week.`

			let payload = {
				first_name: this.first_name.trim(),
				last_name: this.last_name.trim(),
				user_email: this.email.trim(),
				organization: this.organization.trim(),
				organization_type: this.organization_type.trim(),
				org_job_title: this.org_job_title.trim(),
				category_access_requested: this.category_access_requested.trim(),
				reason: this.reason.trim(),

				token_duration: 60*24,
				body_html: email_body_html,
				token_type: 'create_account',
				notify_cglt: `New <a href="https://rosetta.commongoodlt.com">Rosetta Exchange</a> registration request.<br>`
					+ `First name: ${this.first_name.trim()}<br>`
					+ `Last name: ${this.last_name.trim()}<br>`
					+ `Email: ${this.email.trim()}<br>`
					+ `Organization: ${this.organization.trim()}<br>`
					+ `Org. Type: ${this.organization_type.trim()}<br>`
					+ `Org. Title: ${this.org_job_title.trim()}<br>`
					+ `Reason: ${this.reason.trim()}<br>`
					+ `Category access requested: ${this.category_access_requested.trim()}<br>`
			}
			U.loading_start()
			U.ajax('send_token_signin_email', payload, result=>{
				U.loading_stop()
				let msg = ''
				if (result.status != 'ok') {
					console.log(sr('Error in token create account ajax call: $1', result.status))
					this.$alert('An error ocurred in the registration process. Please contact <a href="mailto:support@commongoodlt.com?subject=Error in Rosetta Exchange registration">support@commongoodlt.com</a> for assistance.')

				} else {
					// if we created a new account, start with this
					if (this.user_info.user_id == 0) {
						msg += `A message has been sent to your email address with a link you can click to verify your ${this.site_config.app_name} account and set your password. If you don’t receive the link within one minute, <b>please check your spam or junk folder</b>.<br/><br/>`
					}
					msg += `Your Registration request for download and/or API access to ${this.site_config.app_name} frameworks has been sent to Common Good Learning Tools for review, and you should hear back from us within a week.<br/><br/>`
					msg += `<b>Thank you for your interest in ${this.site_config.app_name}!</b>`
				}
				this.$alert({
					title: 'Registration Request Submitted!',
					text:msg,
					dialogMaxWidth: 500,
				}).then(x=>this.$emit('dialog_cancel'))
			})
		},
	}
}
</script>

<style lang="scss">
</style>
